import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { LoadingIndicator, Pagination } from 'components/common';
import {
  PageEmptyIndicator,
  PageSectionHeader,
  PageSortBy
} from 'components/page';
import { AchievementsTile } from '.';

import type { GetAchievementsRes } from 'shared-types/responses';
import type { ViewProps } from 'types';

const AchievementsViewUser = ({
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 12,
  onNoData = null,
  queryParams = '',
  title
}: ViewProps) => {
  const { messages, user } = useReduxState();

  const router = useRouter();

  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  const { data, loading } = useApiData<GetAchievementsRes>({
    url:
      `/admin/achievements?type=myachievements` +
      `&achievementsPerPage=${limitPerPage}` +
      `&page=${page}` +
      queryParams,
    onlyIf: Boolean(user)
  });

  useEffect(() => {
    const hasData = !loading && Boolean(data?.achievements?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  const handleSortByChange = (value: 'date' | 'title') => {
    router.replace({ query: { ...router.query, sortBy: value } });
  };

  const navigateToAchievementPage = (id: number) => {
    router.push(`/achievements/${id}`, `/achievements/${id}`);
  };

  return loading ? (
    <LoadingIndicator />
  ) : data?.achievements?.length ? (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.achievementsMyAchievements}
        titleCount={data?.achievementsCount}
      >
        <PageSortBy
          defaultValue={router.query.sortBy || 'date'}
          onChange={handleSortByChange}
          options={[
            { text: messages.commonAlphabetic, value: 'title' },
            { text: messages.completionsCompletionDate, value: 'date' }
          ]}
          value={router.query.sortBy || 'date'}
        />
      </PageSectionHeader>

      <div className="achievements-list">
        {data.achievements.map((achievement) => (
          <div
            className="achievement"
            key={achievement.id}
            onClick={() => navigateToAchievementPage(achievement.id)}
            onKeyDown={(ev: React.KeyboardEvent) => {
              if (ev.target !== ev.currentTarget) return;
              // check if enter or spacebar
              if (ev.keyCode === 13 || ev.keyCode === 32) {
                ev.preventDefault();
                navigateToAchievementPage(achievement.id);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <AchievementsTile
              achievement={achievement}
              achievementData={{
                completionDate: achievement.completions?.[0]?.completionDate,
                expirationDate: achievement.completions?.[0]?.expirationDate,
                numberOfCompletedTemplateItems:
                  achievement.completions?.[0]?.numberOfUserTemplateItems,
                status: achievement.completions?.[0]?.status
              }}
              numberOfGroupCompletions={achievement.completions?.length || 0}
              numberOfTemplateItems={achievement.template?.length || 0}
            />
          </div>
        ))}
      </div>

      {data?.pages > 1 && !isHomeView ? (
        <Pagination
          currentPage={page}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page }) => setPage(page)}
        />
      ) : null}

      <style jsx>{`
        .achievements-list {
          display: grid;
          gap: 30px;
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: auto;
          height: 100%;
        }
        @media (max-width: ${breakpoints.large - 1}px) {
          .achievements-list {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .achievements-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  ) : hideIfNoData ? null : (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.achievementsMyAchievements}
        titleCount={0}
      />

      <PageEmptyIndicator
        icon="check-circle"
        title={messages.achievementsEmpty}
        description={messages.achievementsEmpty}
      />
    </>
  );
};

export default AchievementsViewUser;
